import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core"
import { Field, Form, Formik } from "formik"
import { CheckboxWithLabel, TextField } from "formik-material-ui"
import { Autocomplete } from "formik-material-ui-lab"
import { useContext } from "react"
import { useMutation, useQueryClient } from "react-query"
import * as Yup from "yup"
import { createPort } from "~/actions/labs"
import CounterHeader from "~/components/Common/CounterHeader"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const CreateTabDialog = () => {
  const classes = useStyles()

  const queryClient = useQueryClient()
  const { dialogs, closeDialog, openSnackBar } = useContext(AppContext)
  const thisDialog = dialogs?.["createTabDialog"] || {}
  const { open = false, data } = thisDialog

  const tab = data?.lab
  const openErrorSnackbar = useOpenErrorSnackbar()
  const createTabMutation = useMutation((data) => createPort(tab.id, data))

  const handleClose = () => {
    closeDialog("createTabDialog")
  }

  const onSubmit = (values, actions) => {
    createTabMutation.mutate(values, {
      onSuccess: (res) => {
        queryClient.setQueryData(["lab", tab.id], (oldData) => {
          return res
        })
        openSnackBar({ message: "Created Lab Output" })
        handleClose()
      },
      onError: (err) => {
        openErrorSnackbar(err, "Error Creating Output")
      },
      onSettled: () => {
        actions.setSubmitting(false)
      },
    })
  }

  const schema = Yup.object().shape({
    name: Yup.string()
      .required("Please enter a name")
      .max(50, "Name must be less than 50 characters"),
    port: Yup.number()
      .typeError("Please enter a valid port number")
      .required("Please enter a port"),
    lab_instance_id: Yup.number()
      .typeError("Please add an instance")
      .required("Must add an instance"),
  })

  return (
    <Dialog open={open}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h2" className={classes.title}>
          Add Tab Output
        </Typography>
        <Typography color="primary" variant="subtitle2">
          Create a Port
        </Typography>
      </DialogTitle>
      <Formik
        initialValues={{
          name: "",
          port: null,
          lab_instance_id: null,
          check_before_ready: false,
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({ values, isSubmitting, touched, errors }) => (
          <Form>
            <DialogContent>
              <Box>
                <CounterHeader title="Name" value={values.name} max={50} />
                <Field
                  name="name"
                  component={TextField}
                  variant="filled"
                  fullWidth
                />
              </Box>
              <Box width="50%">
                <Typography color="secondary" className={classes.inputLabel}>
                  Port
                </Typography>
                <Field
                  name="port"
                  component={TextField}
                  variant="filled"
                  fullWidth
                />
              </Box>
              <Box>
                <Typography color="secondary" className={classes.inputLabel}>
                  Select Instance
                </Typography>
                <Field
                  name="lab_instance_id"
                  options={tab?.instances?.map((x) => {
                    return x.id
                  })}
                  component={Autocomplete}
                  disableClearable
                  placeholder="Select"
                  getOptionLabel={(x) => {
                    const instance = tab?.instances?.find?.((y) => x === y.id)
                    return instance?.name
                  }}
                  renderInput={(params) => {
                    return (
                      <MuiTextField
                        {...params}
                        name="lab_instance_id"
                        variant="filled"
                        error={
                          !!errors.lab_instance_id && !!touched.lab_instance_id
                        }
                        helperText={
                          <span>
                            {errors.lab_instance_id && touched.lab_instance_id
                              ? errors.lab_instance_id
                              : ""}
                          </span>
                        }
                      />
                    )
                  }}
                  classes={{
                    inputRoot: classes.autocompleteRoot,
                  }}
                />
              </Box>
              <Box marginTop="16px" width="50%">
                <Field
                  type="checkbox"
                  component={CheckboxWithLabel}
                  value={values.check_before_ready}
                  checked={values.check_before_ready}
                  name="check_before_ready"
                  color="secondary"
                  Label={{ label: "Check Before Ready" }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} style={{ height: "40px" }}>
                Cancel
              </Button>
              <LoaderButton
                type="submit"
                classoverrides={{
                  wrapper: classes.button,
                  button: {
                    root: classes.buttonRoot,
                  },
                }}
                variant="contained"
                color="secondary"
                working={isSubmitting}
                size="small"
              >
                Add
              </LoaderButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default CreateTabDialog
